import { createRouter } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import i18n from './i18n'
import { createLocaleAwareHistory } from './i18n/locale-history'
import { bindLocaleToHistory } from './i18n/locale-sync'

/**
 * The locale lives in the URL (`/about`, `/en/about`, `/es/about`) but not in
 * the route tree: this history strips the locale segment before the router
 * matches, and puts it back on every href the router builds. See
 * `i18n/locale-history.ts` for why the translation happens here rather than in
 * three parallel copies of every route.
 */
export const localeHistory = createLocaleAwareHistory(window)

bindLocaleToHistory(i18n, localeHistory)

// Create the router with the route tree
export const router = createRouter({
  routeTree,
  history: localeHistory.history,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: ({ error }: { error: Error }) => (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <h1 className="text-4xl font-bold text-destructive mb-4">Error</h1>
      <p className="text-lg text-muted-foreground">{error.message}</p>
      <a
        href="/"
        className="mt-6 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
      >
        Go Home
      </a>
    </div>
  ),
  defaultNotFoundComponent: () => (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <h1 className="text-4xl font-bold mb-4">404 - Page Not Found</h1>
      <p className="text-lg text-muted-foreground">The page you're looking for doesn't exist.</p>
      <a
        href="/"
        className="mt-6 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
      >
        Go Home
      </a>
    </div>
  ),
})

// Export types for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
